import type { DriverTarget } from "@skaile/workspaces/plugin-registry";
import type { Logger } from "@skaile/workspaces/types";
import * as z from "zod";
import { getBridgeLogger } from "../logger.js";
import type { ModelEntry } from "../models.js";
import { BUILTIN_DRIVER_CATALOG } from "../registry.js";
import type { AgentConfig, DriverInfo } from "../types.js";
import {
  extractCost,
  extractText,
  extractTokens,
  mapApiKeysToEnv,
  SubprocessCliDriver,
} from "./_subprocess-cli.js";

/**
 * Agent driver for **OpenCode** (`opencode`), the model-agnostic open-source
 * coding agent.
 *
 * Spawns `opencode run "<prompt>" --format json` per turn and maps OpenCode's
 * JSON event stream to the unified {@link AgentEvent} stream. Model-agnostic:
 * the model is passed as `provider/model` via `-m`, and provider API keys are
 * forwarded through the environment.
 *
 * @remarks
 * - The prompt is a **positional argument** — OpenCode's `-p` flag is a
 *   basic-auth password, not the prompt.
 * - Session resume via `-s <id>`; the session id is captured from the run's
 *   session event and emitted as `session_info`.
 * - OpenCode's `run --format json` event field names are not fully pinned
 *   upstream; {@link handleJson} reads them defensively.
 *
 * @see https://github.com/sst/opencode
 * @docLink packages/bridge/drivers#opencode-driver
 */
export class OpenCodeDriver extends SubprocessCliDriver {
  readonly driverInfo: DriverInfo = {
    id: "opencode",
    name: "OpenCode",
    modelAgnostic: true,
    supportsInBandAbort: false,
  };

  protected readonly log: Logger = getBridgeLogger("opencode");
  protected readonly defaultBin = "opencode";
  protected readonly productName = "OpenCode";
  protected readonly envPrefix = "OPENCODE";

  protected buildArgs(message: string, resumeId: string | undefined): string[] {
    const args = ["run", message, "--format", "json"];
    const model =
      this.config.provider && this.config.model
        ? `${this.config.provider}/${this.config.model}`
        : this.config.model;
    if (model) args.push("-m", model);
    if (resumeId) args.push("-s", resumeId);
    return args;
  }

  protected buildEnv(): Record<string, string> {
    const env = this.baseEnv([
      "ANTHROPIC_API_KEY",
      "OPENAI_API_KEY",
      "GEMINI_API_KEY",
      "OPENROUTER_API_KEY",
      "GROQ_API_KEY",
    ]);
    return { ...env, ...mapApiKeysToEnv(this.config.apiKeys) };
  }

  protected installHint(): string {
    return "OpenCode is not installed or not on your PATH. Install it with `npm i -g opencode-ai`.";
  }

  protected handleJson(json: any): void {
    switch (json?.type) {
      case "session":
      case "session.start":
      case "init":
        this.captureSession(json.id ?? json.sessionId ?? json.session?.id);
        break;

      case "message":
      case "message.part":
      case "part": {
        const role = json.role ?? json.message?.role ?? "assistant";
        if (role !== "assistant") break;
        const text = extractText(json);
        if (json.done === true || json.final === true) this.pushAssistantEnd(text);
        else this.pushAssistantDelta(text);
        break;
      }

      case "tool":
      case "tool_use":
      case "tool.start":
        this.pushToolCall(json.name ?? json.tool ?? "tool", json.input ?? json.args ?? {});
        break;

      case "tool_result":
      case "tool.end":
        this.pushToolResult(
          json.name ?? json.tool ?? "tool",
          json.result ?? json.output ?? json.content,
          Boolean(json.is_error ?? json.error),
        );
        break;

      case "error":
        this.pushError(this.classifyMessage(String(json.message ?? json.error ?? "error")));
        break;

      case "result":
      case "done":
      case "finish":
        this.captureSession(json.sessionId ?? json.session_id);
        this.pushResult({
          tokens: extractTokens(json),
          costUsd: extractCost(json),
          summary: extractText(json) || "Task completed",
        });
        break;
    }
  }

  override async listModels(): Promise<ModelEntry[]> {
    return [];
  }
}

/**
 * `DriverTarget` wrapper for the opencode driver. Registered into the plugin
 * registry by `registerBuiltinDrivers()`.
 */
export const opencodeDriverTarget: DriverTarget<AgentConfig> = {
  id: "opencode",
  displayName: BUILTIN_DRIVER_CATALOG.opencode?.name ?? "OpenCode",
  apiVersion: 1,
  configSchema: z.custom<AgentConfig>(() => true),
  create: async (config, _ctx) => new OpenCodeDriver(config),
};
